import React from "react";
import { Grid, Paper, Table, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { formatAmount, hasPnlActivity } from "./financialStatementsUtils";

const amountCellStyle = { textAlign: "right", whiteSpace: "nowrap" };

const SectionHeaderRow = ({ label }) => (
  <tr>
    <td colSpan={3}>
      <Text size="xs" weight={700} transform="uppercase" color="dimmed">
        {label}
      </Text>
    </td>
  </tr>
);

const SubtotalRow = ({ label, amount, bold = false }) => (
  <tr>
    <td colSpan={2}>
      <Text weight={bold ? 700 : 600}>{label}</Text>
    </td>
    <td style={amountCellStyle}>
      <Text weight={bold ? 700 : 600}>{formatAmount(amount)}</Text>
    </td>
  </tr>
);

const ProfitAndLossView = ({ report, loading = false, periodLabel = "" }) => {
  const { t } = useTranslation();

  if (loading && !report) {
    return (
      <Text size="sm" color="dimmed">
        {t("accounting.financialStatements.loading")}
      </Text>
    );
  }

  if (!report) return null;

  const totalRevenue = Number(report.totalRevenue ?? 0);
  const totalExpenses = Number(report.totalExpenses ?? 0);
  const netProfit = Number(report.netProfit ?? 0);
  const hasActivity = hasPnlActivity(report);

  return (
    <>
      <Paper withBorder p="md" mb="md">
        <Text size="xs" color="dimmed" mb="sm" transform="uppercase" weight={600}>
          {t("accounting.financialStatements.summaryTitle")}
        </Text>
        <Grid>
          <Grid.Col span={4}>
            <Text size="xs" color="dimmed">
              {t("accounting.financialStatements.totalRevenue")}
            </Text>
            <Text weight={700} size="lg">
              {formatAmount(totalRevenue)}
            </Text>
          </Grid.Col>
          <Grid.Col span={4}>
            <Text size="xs" color="dimmed">
              {t("accounting.financialStatements.totalExpenses")}
            </Text>
            <Text weight={700} size="lg">
              {formatAmount(totalExpenses)}
            </Text>
          </Grid.Col>
          <Grid.Col span={4}>
            <Text size="xs" color="dimmed">
              {t("accounting.financialStatements.netProfit")}
            </Text>
            <Text weight={700} size="lg" color={netProfit >= 0 ? "green" : "red"}>
              {formatAmount(netProfit)}
            </Text>
          </Grid.Col>
        </Grid>
      </Paper>

      <Paper withBorder p="md">
        <Text weight={700} size="lg" mb={4}>
          {t("accounting.financialStatements.pnlTitle")}
        </Text>
        {periodLabel && (
          <Text size="sm" color="dimmed" mb="md">
            {periodLabel}
          </Text>
        )}

        {!hasActivity ? (
          <Text size="sm" color="dimmed">
            {t("accounting.financialStatements.noActivity")}
          </Text>
        ) : (
          <Table striped highlightOnHover>
            <thead>
              <tr>
                <th>{t("Code")}</th>
                <th>{t("accounting.journal.account")}</th>
                <th style={amountCellStyle}>{t("Amount")}</th>
              </tr>
            </thead>
            <tbody>
              <SectionHeaderRow label={t("accounting.financialStatements.revenue")} />
              {(report.revenue || []).map((row) => (
                <tr key={`rev-${row.code}`}>
                  <td>{row.code}</td>
                  <td>{row.name}</td>
                  <td style={amountCellStyle}>{formatAmount(row.amount)}</td>
                </tr>
              ))}
              <SubtotalRow
                label={t("accounting.financialStatements.totalRevenue")}
                amount={totalRevenue}
              />

              <SectionHeaderRow label={t("accounting.financialStatements.expenses")} />
              {(report.expenses || []).map((row) => (
                <tr key={`exp-${row.code}`}>
                  <td>{row.code}</td>
                  <td>{row.name}</td>
                  <td style={amountCellStyle}>{formatAmount(row.amount)}</td>
                </tr>
              ))}
              <SubtotalRow
                label={t("accounting.financialStatements.totalExpenses")}
                amount={totalExpenses}
              />

              <SubtotalRow
                label={t("accounting.financialStatements.netProfit")}
                amount={netProfit}
                bold
              />
            </tbody>
          </Table>
        )}
      </Paper>
    </>
  );
};

export default ProfitAndLossView;
